import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  EnrollmentConfigState,
  EnrollmentTargetKind,
  EnrollmentTargetsState,
  EnrollmentTriggerPayload,
} from "./types";
import {
  buildInitialConfig,
  validateConfig,
} from "./config/enrollmentFieldsConfig";
import { openToast } from "@utils/toastStore";
import TargetSelectionStep from "./steps/TargetSelectionStep";
import ConfigurationStep from "./steps/ConfigurationStep";
import { useEnrollmentSubmit } from "./hooks/useEnrollmentSubmit";
import { useAttributesList } from "./hooks/useAttributesList";

interface EnrollmentModalProps {
  trigger: EnrollmentTriggerPayload;
  onClose: () => void;
}

type Step = "targets" | "config";

const initialTargets: EnrollmentTargetsState = {
  users: [],
  groups: [],
  initiatives: [],
};

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const theme = useTheme();

  const [step, setStep] = useState<Step>("targets");
  const [activeKind, setActiveKind] = useState<EnrollmentTargetKind>("users");
  const [targets, setTargets] =
    useState<EnrollmentTargetsState>(initialTargets);
  const [config, setConfig] =
    useState<EnrollmentConfigState>(buildInitialConfig);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const { options: attributes } = useAttributesList(trigger.corporateId);
  const { submit, isSubmitting } = useEnrollmentSubmit();

  const totalSelectedCount =
    targets.users.length + targets.groups.length + targets.initiatives.length;
  const canProceedToConfig = totalSelectedCount > 0;

  const validation = useMemo(() => validateConfig(config), [config]);

  const handleTargetsChange = (
    kind: EnrollmentTargetKind,
    items: typeof targets.users,
  ) => setTargets((prev) => ({ ...prev, [kind]: items }));

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    if (!validation.valid) return;
    try {
      await submit({ trigger, targets, config });
      trigger.onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: t("feedback.success") || "Iscrizione completata con successo",
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message:
          e?.message || t("feedback.error") || "Errore durante l'iscrizione",
      });
    }
  };

  const title = trigger.objectTitle
    ? `${t("title") || "Iscrivi"} — ${trigger.objectTitle}`
    : t("title") || "Iscrivi";

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          disabled={isSubmitting}
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "8px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        {step === "targets" ? (
          <TargetSelectionStep
            trigger={trigger}
            activeKind={activeKind}
            onActiveKindChange={(k) => {
              setActiveKind(k);
            }}
            targets={targets}
            onTargetsChange={handleTargetsChange}
          />
        ) : (
          <ConfigurationStep
            trigger={trigger}
            config={config}
            onChange={setConfig}
            attributes={attributes}
            errors={attemptedSubmit ? validation.errors : {}}
            targets={targets}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        {step === "config" && (
          <Button
            variant="outlined"
            onClick={() => setStep("targets")}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            {t("actions.back") || "Indietro"}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step === "targets" ? (
          <Button
            variant="contained"
            onClick={() => setStep("config")}
            disabled={!canProceedToConfig}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            {t("actions.next") || "Avanti"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            {isSubmitting
              ? t("actions.submitting") || "Iscrizione in corso…"
              : t("actions.submit") || "Iscrivi"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnrollmentModal;
